import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { DiscountPolicy } from 'src/features/discountPolicies/utils/types';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import { useDeleteDiscountPolicy } from 'src/features/discountPolicies/api/useDeleteDiscountPolicy';
import { LIST_DISCOUNT_POLICIES_RESOURCE } from 'src/features/discountPolicies/api/useListDiscountPolicies';
import { useNavigate } from 'react-router-dom';

interface DeleteDiscountPolicyFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPolicy: DiscountPolicy;
    children?: React.ReactNode;
}

function DeleteDiscountPolicyFormModal<TData>({ selectedPolicy, children }: DeleteDiscountPolicyFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deleteDiscountPolicy } = useDeleteDiscountPolicy({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_DISCOUNT_POLICIES_RESOURCE] });
            setIsModalOpen(false);
            navigate('/discount-policies');
            toast({
                title: formatMessage(discountPoliciesMessages.deleteDiscountPolicySuccess),
                description: formatMessage(discountPoliciesMessages.deleteDiscountPolicySuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...discountPoliciesMessages.deleteDiscountPolicy },
                subTitle: {
                    ...discountPoliciesMessages.deleteDiscountPolicySubtitle,
                    values: {
                        name: selectedPolicy.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...discountPoliciesMessages.confirmDeleteDiscountPolicy },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: discountPoliciesMessages.deleteDiscountPolicy,
                callbackFn: () => {
                    return (
                        deleteDiscountPolicy({
                            policyId: selectedPolicy.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteDiscountPolicyFormModal;
